import type { ComputedRef, Ref } from 'vue'
import { isTerminalFinancingStatus } from '~/composables/financing/useTerminalStatusLock'
import { GeneralStatuses } from '~/utils/financing-flow/Financing'

/**
 * Composable to manage equipment editing permissions and validations
 *
 * Implements cascade logic: When a stage is approved, check the next stage
 * - RULE 1: Contract not sent → EVERYONE can edit
 * - RULE 2: Contract sent but not signed → EVERYONE blocked (must cancel)
 * - RULE 3: Biometry & Contract approved → Check payment_model status
 * - RULE 4: Payment Model approved → Check installation status
 */

interface UseEquipmentEditPermissionsParams {
  financing: Ref<any>
  customerBiometry: Ref<any>
  isContractSigned: Ref<boolean | undefined>
  auth: Ref<any>
  isEditingEquipment: ComputedRef<boolean>
  hasPendingTAF: ComputedRef<boolean>
}

interface UseEquipmentEditPermissionsReturn {
  isAdmin: ComputedRef<boolean>
  isProjectCanceledOrExpired: ComputedRef<boolean>
  isBiometryAndContractApproved: ComputedRef<boolean>
  isPaymentModelApproved: ComputedRef<boolean>
  hasContractBeenGenerated: ComputedRef<boolean>
  isProjectApproved: ComputedRef<boolean>
  isEditButtonDisabled: ComputedRef<boolean>
  editButtonTooltipText: ComputedRef<string>
  showContractUpdateEquipmentAlert: ComputedRef<boolean>
}

export function useEquipmentEditPermissions(
  params: UseEquipmentEditPermissionsParams,
): UseEquipmentEditPermissionsReturn {
  const {
    financing,
    customerBiometry,
    isContractSigned,
    auth,
    isEditingEquipment,
    hasPendingTAF,
  } = params

  const isAdmin = computed(() => auth.value?.me?.perfil === 'admin')

  // Any terminal financing status (expired / reproved / canceled); name kept for compatibility.
  const isProjectCanceledOrExpired = computed(
    () => isTerminalFinancingStatus(financing.value?.status),
  )

  /**
   * RULE 3: Checks if biometry and contract section is approved
   * Now EVERYONE can edit (not just admin), but generates TAF
   */
  const isBiometryAndContractApproved = computed(
    () => financing.value?.section_statuses?.biometry_and_contract?.status === GeneralStatuses.APPROVED,
  )

  /**
   * Checks if payment_model section is approved
   */
  const isPaymentModelApproved = computed(
    () => financing.value?.section_statuses?.payment_model?.status === GeneralStatuses.APPROVED,
  )

  /**
   * Checks if project is approved (final status)
   */
  const isProjectApproved = computed(
    () => financing.value?.status === GeneralStatuses.APPROVED,
  )

  /**
   * Checks if payment_model is in a status that allows equipment editing
   * Must be 'in_progress' or 'to_review' when biometry_and_contract is approved
   */
  const isPaymentModelEditable = computed(() => {
    const paymentModelStatus = financing.value?.section_statuses?.payment_model?.status
    return ['in_progress', 'to_review'].includes(paymentModelStatus ?? '')
  })

  /**
   * Checks if installation is in a status that allows equipment editing
   * Must be 'in_progress' or 'to_review' when payment_model is approved
   */
  const isInstallationEditable = computed(() => {
    const installationStatus = financing.value?.section_statuses?.installation?.status
    return [
      GeneralStatuses.IN_PROGRESS,
      GeneralStatuses.TO_REVIEW,
      GeneralStatuses.APPROVED,
    ].includes(installationStatus as GeneralStatuses)
  })

  /**
   * Checks if the contract has been generated or sent
   * Valid statuses before sending: 'waiting_to_start', 'in_progress'
   * Any other status means the contract has already been sent/processed
   */
  const hasContractBeenGenerated = computed(() => {
    if (!customerBiometry.value?.status) {
      return false
    }

    // If status is NOT 'waiting_to_start' or 'in_progress', it means it has already been sent
    const contractNotSent = ['waiting_to_start', 'in_progress'].includes(customerBiometry.value.status)

    return !contractNotSent || isContractSigned.value === true
  })

  /**
   * Checks if the edit button should be disabled
   *
   * Cascade logic: When a stage is approved, check the next stage
   * RULE 1: Contract not sent → EVERYONE can edit
   * RULE 2: Contract sent but not signed → EVERYONE blocked (must cancel)
   * RULE 3: Biometry & Contract approved → Check payment_model status
   * RULE 4: Payment Model approved → Check installation status
   */
  const isEditButtonDisabled = computed(() => {
    // If there's a pending TAF (awaiting signature), EVERYONE is blocked
    if (hasPendingTAF.value) {
      return true
    }

    // If project is in a terminal status (expired / reproved / canceled), EVERYONE is blocked
    if (isProjectCanceledOrExpired.value) {
      return true
    }

    // RULE 2: Contract sent but not approved (can still cancel easily)
    // EVERYONE is blocked
    if (hasContractBeenGenerated.value && !isBiometryAndContractApproved.value) {
      return true
    }

    // RULE 4: Payment Model approved → Check installation status
    // When payment_model is approved, only look at installation
    if (isPaymentModelApproved.value) {
      return !isInstallationEditable.value
    }

    // RULE 3: Biometry & Contract approved → Check payment_model status
    // When biometry_and_contract is approved, only look at payment_model
    if (isBiometryAndContractApproved.value) {
      return !isPaymentModelEditable.value
    }

    // RULE 1: Contract not sent: EVERYONE can edit
    return false
  })

  /**
   * Tooltip text for the edit button
   * Returns appropriate text based on why the button is disabled
   * Uses line breaks (\n) for better visual formatting
   */
  const editButtonTooltipText = computed(() => {
    if (!isEditButtonDisabled.value) {
      return ''
    }

    // Pending TAF (awaiting customer signature)
    if (hasPendingTAF.value) {
      return `Há uma alteração de equipamentos pendente.
Aguarde o cliente assinar o Termo de Aditamento de
Financiamento (TAF) para poder editar novamente.
`
    }

    // Project in a terminal status (everyone blocked)
    if (isProjectCanceledOrExpired.value) {
      if (financing.value?.status === 'reproved')
        return 'O projeto está reprovado.'

      return 'O projeto está cancelado ou expirado.'
    }

    // Project approved (everyone blocked)
    if (isProjectApproved.value) {
      return 'O projeto está aprovado. Você não pode editar os equipamentos.'
    }

    // RULE 2: Contract sent but not approved (everyone blocked - admin and partner)
    if (hasContractBeenGenerated.value && !isBiometryAndContractApproved.value) {
      return `Não é possível editar os equipamentos, pois a etapa de biometria e contrato já foi inciada.
Para alterar, você precisa cancelar a etapa de biometria e contrato atual.
`
    }

    // RULE 4: Payment Model approved but installation is not in editable status
    if (isPaymentModelApproved.value && !isInstallationEditable.value) {
      return `Os equipamentos só podem ser editados quando a etapa de instalação estiver em andamento, em revisão ou concluída.
`
    }

    // RULE 3: Biometry & Contract approved but payment_model is not in editable status
    if (isBiometryAndContractApproved.value && !isPaymentModelEditable.value) {
      return `Os equipamentos só podem ser editados quando o modelo de recebimento estiver em andamento ou em revisão.
`
    }

    return ''
  })

  /**
   * Checks if contract amendment alert should be shown
   * RULE 3: Shows when ANY user is editing equipment with approved contract
   */
  const showContractUpdateEquipmentAlert = computed(() => {
    return isBiometryAndContractApproved.value && isEditingEquipment.value
  })

  return {
    isAdmin,
    isProjectCanceledOrExpired,
    isBiometryAndContractApproved,
    isPaymentModelApproved,
    hasContractBeenGenerated,
    isProjectApproved,
    isEditButtonDisabled,
    editButtonTooltipText,
    showContractUpdateEquipmentAlert,
  }
}
